import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  purchaseRequestId: string;
  authorId: string;
  targetUserId: string;
  targetUserName: string;
  onReviewSubmitted: () => void;
}

export function PurchaseReviewModal({
  open,
  onOpenChange,
  purchaseRequestId,
  authorId,
  targetUserId,
  targetUserName,
  onReviewSubmitted,
}: Props) {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (rating < 1) return;
    setLoading(true);

    // Block if a review already exists between this user pair (in either direction from author).
    const { data: existing } = await supabase
      .from("purchase_reviews")
      .select("id")
      .eq("author_id", authorId)
      .eq("target_user_id", targetUserId)
      .maybeSingle();

    if (existing) {
      setLoading(false);
      toast.error("Du har redan lämnat ett omdöme på den här användaren.");
      onOpenChange(false);
      onReviewSubmitted();
      return;
    }

    const { error } = await supabase.from("purchase_reviews").insert({
      purchase_request_id: purchaseRequestId,
      author_id: authorId,
      target_user_id: targetUserId,
      rating,
      text: text.trim() || null,
    });
    setLoading(false);
    if (error) {
      toast.error("Kunde inte skicka recensionen");
    } else {
      toast.success("Din recension har skickats!");
      setRating(0);
      setText("");
      onOpenChange(false);
      onReviewSubmitted();
    }
  };

  const displayRating = hoveredRating || rating;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) {
          setRating(0);
          setHoveredRating(0);
          setText("");
        }
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Lämna en recension</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Din recension av {targetUserName}
          </p>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {/* Star rating */}
          <div className="flex justify-center gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                className="p-1 transition-transform hover:scale-110"
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
                onClick={() => setRating(star)}
              >
                <Star
                  className={`h-9 w-9 transition-colors ${
                    star <= displayRating
                      ? "fill-yellow-400 text-yellow-400"
                      : "text-muted-foreground/40"
                  }`}
                />
              </button>
            ))}
          </div>

          {/* Comment */}
          <Textarea
            placeholder="Berätta om din upplevelse (valfritt)"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
          />

          {/* Submit */}
          <Button
            className="w-full bg-green-600 hover:bg-green-700 text-white"
            disabled={rating < 1 || loading}
            onClick={handleSubmit}
          >
            Skicka recension
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
